import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  AUTH_SESSION_EXPIRED_EVENT,
  AUTH_SESSION_CLEARED_EVENT,
  AUTH_TOKENS_UPDATED_EVENT,
  AUTH_API_BASE,
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  initAuthTokenCrossTabSync,
  refreshAuthTokens,
  setAuthTokens,
} from '../services/authTokenStore';
import { clearMarketProfileSession } from '../components/Community/marketProfile/marketProfileStore';

type AuthUser = {
  id: string;
  email: string | null;
  phone?: string | null;
  user_metadata?: {
    name?: string;
    full_name?: string;
    display_name?: string;
  };
};

type AuthSession = {
  accessToken: string;
};

type AuthResponse = {
  user: {
    id: string;
    login: string;
    email: string;
    phone?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    display_name?: string | null;
  };
  accessToken: string;
  refreshToken: string;
};

interface AuthContextType {
  user: AuthUser | null;
  session: AuthSession | null;
  loading: boolean;
  sessionExpiredMessage: string | null;
  clearSessionExpiredMessage: () => void;
  signUp: (name: string, email: string, password: string) => Promise<{ user: AuthUser | null; session: AuthSession | null }>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);

  const mapBackendUser = (apiUser: AuthResponse['user']): AuthUser => ({
    id: apiUser.id,
    email: apiUser.email,
    phone: apiUser.phone ?? null,
    user_metadata: {
      name: apiUser.display_name || apiUser.first_name || apiUser.login,
      full_name: [apiUser.first_name, apiUser.last_name].filter(Boolean).join(' ') || apiUser.display_name || apiUser.login,
      display_name: apiUser.display_name || apiUser.login,
    },
  });

  const parseErrorMessage = async (response: Response): Promise<string> => {
    try {
      const data = await response.json();
      if (typeof data?.error === 'string' && data.error.trim().length > 0) return data.error;
    } catch {
      // ignore JSON parse error
    }
    return `Request failed with status ${response.status}`;
  };

  const getMe = async (accessToken: string): Promise<AuthUser> => {
    const response = await fetch(`${AUTH_API_BASE}/auth/me`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }
    const data = await response.json();
    return mapBackendUser(data.user);
  };

  useEffect(() => {
    const cleanupCrossTab = initAuthTokenCrossTabSync();

    const onSessionExpired = () => {
      clearMarketProfileSession();
      setUser(null);
      setSession(null);
      setSessionExpiredMessage('Сессия истекла. Войдите снова.');
    };

    const onSessionCleared = () => {
      clearMarketProfileSession();
      setUser(null);
      setSession(null);
    };

    const onTokensUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ accessToken?: string }>).detail;
      const accessToken = detail?.accessToken || getAccessToken();
      if (accessToken) {
        setSession({ accessToken });
        setSessionExpiredMessage(null);
      }
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    window.addEventListener(AUTH_SESSION_CLEARED_EVENT, onSessionCleared);
    window.addEventListener(AUTH_TOKENS_UPDATED_EVENT, onTokensUpdated as EventListener);

    const forgetInvalidSession = () => {
      clearAuthTokens();
      clearMarketProfileSession();
      setUser(null);
      setSession(null);
    };

    const initAuth = async () => {
      const accessToken = getAccessToken();
      if (accessToken) {
        try {
          const me = await getMe(accessToken);
          setUser(me);
          setSession({ accessToken });
          setLoading(false);
          return;
        } catch (error) {
          const message = error instanceof Error ? error.message.toLowerCase() : '';
          // Аккаунт удалили, JWT в браузере ещё жив — тихо снимаем сессию, без «Сессия истекла».
          if (message.includes('user not found')) {
            forgetInvalidSession();
            setLoading(false);
            return;
          }
        }
      }

      if (!getRefreshToken()) {
        if (getAccessToken()) forgetInvalidSession();
        else setUser(null);
        setLoading(false);
        return;
      }

      const refreshed = await refreshAuthTokens({ notifyOnFailure: false });
      if (refreshed) {
        const nextAccessToken = getAccessToken();
        if (nextAccessToken) {
          try {
            const me = await getMe(nextAccessToken);
            setUser(me);
            setSession({ accessToken: nextAccessToken });
          } catch {
            forgetInvalidSession();
          }
        }
      } else {
        forgetInvalidSession();
      }
      setLoading(false);
    };

    void initAuth();

    return () => {
      cleanupCrossTab();
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
      window.removeEventListener(AUTH_SESSION_CLEARED_EVENT, onSessionCleared);
      window.removeEventListener(AUTH_TOKENS_UPDATED_EVENT, onTokensUpdated as EventListener);
    };
  }, []);

  const signUp = async (name: string, email: string, password: string) => {
    const loginBase = email.split('@')[0].toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, 40) || 'user';
    const login = `${loginBase}_${Math.floor(Math.random() * 10000)}`;

    const response = await fetch(`${AUTH_API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        login,
        email,
        password,
        displayName: name,
        firstName: name,
      }),
    });

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }
    const data = (await response.json()) as AuthResponse;
    const mappedUser = mapBackendUser(data.user);
    clearMarketProfileSession();
    setAuthTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    setUser(mappedUser);
    setSessionExpiredMessage(null);
    return { user: mappedUser, session: { accessToken: data.accessToken } };
  };

  const signIn = async (email: string, password: string) => {
    const response = await fetch(`${AUTH_API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identity: email, password }),
    });

    if (!response.ok) {
      throw new Error(await parseErrorMessage(response));
    }
    const data = (await response.json()) as AuthResponse;
    clearMarketProfileSession();
    setAuthTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    setUser(mapBackendUser(data.user));
    setSessionExpiredMessage(null);
  };

  const signOut = async () => {
    clearAuthTokens();
    clearMarketProfileSession();
    setUser(null);
    setSession(null);
    setSessionExpiredMessage(null);
  };

  const value = {
    user,
    session,
    loading,
    sessionExpiredMessage,
    clearSessionExpiredMessage: () => setSessionExpiredMessage(null),
    signUp,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
